import * as React from 'react';
import { RadioGroup, Radio } from '@adobe/react-spectrum';
import type { IAtollonTableColumn } from '../../../../ui/Table';
import { AtollonTable } from '../../../../ui/Table';
import ChevronUp from '@spectrum-icons/workflow/ChevronUp';
import ChevronDown from '@spectrum-icons/workflow/ChevronDown';
import { AtollonButton, AtollonButtonDesignType } from '../../../../ui/Button/AtollonButton';
import styles from './SortingTable.module.css';
import { TrashIcon } from '../../../Icons/IconsLib';

export interface ISortingRow {
  column: string;
  itemname: string;
  level: 'Ascending' | 'Descending';
}

export interface ISortingTableProps {
  data?: ISortingRow[];
  onDataChange?: (data: ISortingRow[]) => void;
  onReorder?: (keys: string[]) => void;
}

// Static default data based on screenshot

export function SortingTable(props: ISortingTableProps) {
  const { data = [], onDataChange, onReorder } = props;
  const [rows, setRows] = React.useState<ISortingRow[]>(data);

  React.useEffect(() => {
    setRows(data);
  }, [data]);

  const handleLevelChange = React.useCallback((rowItemname: string, level: 'Ascending' | 'Descending') => {
    const updatedRows = rows.map(row => 
      row.itemname === rowItemname ? { ...row, level } : row
    );
    setRows(updatedRows);
    onDataChange?.(updatedRows);
  }, [rows, onDataChange]);

  const handleDelete = React.useCallback((rowItemname: string) => {
    const updatedRows = rows.filter(row => row.itemname !== rowItemname);
    setRows(updatedRows);
    onDataChange?.(updatedRows);
  }, [rows, onDataChange]);

  const handleReorder = React.useCallback((keys: string[]) => {
    const reorderedRows = keys.map(key => rows.find(row => row.itemname === key)).filter(Boolean) as ISortingRow[];
    setRows(reorderedRows);
    onDataChange?.(reorderedRows);
    onReorder?.(keys);
  }, [rows, onDataChange, onReorder]);

  const handleMoveUp = React.useCallback((rowItemname: string) => {
    const currentIndex = rows.findIndex(row => row.itemname === rowItemname);
    if (currentIndex <= 0) return; // Already at top or not found

    const newRows = [...rows];
    [newRows[currentIndex - 1], newRows[currentIndex]] = [newRows[currentIndex], newRows[currentIndex - 1]];
    setRows(newRows);
    onDataChange?.(newRows);
    onReorder?.(newRows.map(row => row.itemname));
  }, [rows, onDataChange, onReorder]);

  const handleMoveDown = React.useCallback((rowItemname: string) => {
    const currentIndex = rows.findIndex(row => row.itemname === rowItemname);
    if (currentIndex < 0 || currentIndex >= rows.length - 1) return; // Already at bottom or not found

    const newRows = [...rows];
    [newRows[currentIndex], newRows[currentIndex + 1]] = [newRows[currentIndex + 1], newRows[currentIndex]];
    setRows(newRows);
    onDataChange?.(newRows);
    onReorder?.(newRows.map(row => row.itemname));
  }, [rows, onDataChange, onReorder]);

  const columns: IAtollonTableColumn<ISortingRow>[] = [
    {
      key: 'reorder',
      label: '',
      width: 40,
      renderCell: (row) => {
        const currentIndex = rows.findIndex(r => r.itemname === row.itemname);
        const isFirst = currentIndex === 0;
        const isLast = currentIndex === rows.length - 1;

        return (
          <div className={styles.reorderButtons}>
            <AtollonButton
              designType={AtollonButtonDesignType.LINK}
              onPress={() => handleMoveUp(row.itemname)}
              isDisabled={isFirst}
              aria-label={`Move ${row.column} up`}
            >
              <ChevronUp size="S" />
            </AtollonButton>
            <AtollonButton
              designType={AtollonButtonDesignType.LINK}
              onPress={() => handleMoveDown(row.itemname)}
              isDisabled={isLast}
              aria-label={`Move ${row.column} down`}
            >
              <ChevronDown size="S" />
            </AtollonButton>
          </div>
        );
      },
    },
    {
      key: 'column',
      label: 'Column',
      dataKey: 'column',
      width: 'auto',
    },
    {
      key: 'level',
      label: 'Level',
      width: 250,
      renderCell: (row) => (
        <RadioGroup
          value={row.level}
          onChange={(value) => handleLevelChange(row.itemname, value as 'Ascending' | 'Descending')}
          orientation="horizontal"
          aria-label={`Sort level for ${row.column}`}
        >
          <Radio value="Ascending">Ascending</Radio>
          <Radio value="Descending">Descending</Radio>
        </RadioGroup>
      ),
    },
    {
      key: 'delete',
      label: '',
      width: 40,
      renderCell: (row) => (
        <button
          className={styles.deleteButton}
          onClick={() => handleDelete(row.itemname)}
          aria-label={`Delete ${row.column}`}
          type="button"
        >
          <TrashIcon size={24} />
        </button>
      ),
    },
  ];

  return (
    <div className={styles.sortingTable}>
      <AtollonTable
        columns={columns}
        rows={rows}
        getRowKey={(row) => row.itemname}
        onReorder={handleReorder}
        aria-label="Sorting configuration table"
      />
    </div>
  );
}

